import { Span, Text, TextButton, Title } from "@clipboard-health/ui-components";
import { Box, DialogContent, Link, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { type TouchpointCodeGeneratorProps } from "@src/appV2/Shifts/Shift/TouchpointIntegration/touchpointCodeGenerator";

import { TouchpointQrCode } from "../components/TouchpointQrCode";

export interface QrCodeDisplayProps {
  qrCodeProps: TouchpointCodeGeneratorProps;
  kioskLocation: string;
  secondsLeft: number;
  onQrCodeReady?: () => void;
  onSkip: () => void;
  onCancel: () => void;
}

export function QrCodeDisplay(props: QrCodeDisplayProps) {
  const { qrCodeProps, kioskLocation, secondsLeft, onQrCodeReady, onSkip, onCancel } = props;

  const { showErrorToast } = useToast();

  const { punchType, shiftId, facilityId, agentId } = qrCodeProps;

  const actionText = punchType === "clock-in" ? "in" : "out";
  const isSkipButtonEnabled = secondsLeft <= 0;

  return (
    <>
      <DialogContent sx={{ paddingX: 6, paddingTop: 8, paddingBottom: 2, textAlign: "center" }}>
        <Stack spacing={6}>
          <Stack spacing={4}>
            <Title semibold variant="h4" component="h4">
              Scan QR Code at kiosk
            </Title>
            <Text variant="body1" color={(theme) => theme.palette.text.secondary}>
              Clock {actionText} by scanning this QR code at the kiosk located at{" "}
              <Span style={{ fontWeight: "500" }}>{kioskLocation}</Span>.
            </Text>
          </Stack>

          <TouchpointQrCode qrCodeProps={qrCodeProps} onQrCodeReady={onQrCodeReady} />

          <Box>
            <TextButton
              LinkComponent={Link}
              onClick={async () => {
                logEvent(APP_V2_USER_EVENTS.TOUCHPOINT_TUTORIAL_OPENED, {
                  shiftId,
                  facilityId,
                  workerId: agentId,
                  action: punchType,
                  secondsLeft,
                });
                await openInBrowser({ url: "https://www.youtube.com/watch?v=vAZO2xJ3yUM" });
              }}
            >
              Please watch this video tutorial.
            </TextButton>
          </Box>
        </Stack>
      </DialogContent>
      <DialogFooter
        orientation="vertical"
        closeButtonAriaLabel="Go back"
        closeButtonIconType="arrow-left"
        onClose={onCancel}
      >
        <Box
          sx={{ width: "100%" }}
          onClick={() => {
            if (!isSkipButtonEnabled) {
              showErrorToast(
                "Please contact facility staff if you cannot find the kiosk or it is not working."
              );
            }
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            size="large"
            disabled={!isSkipButtonEnabled}
            onClick={onSkip}
          >
            Skip QR scan
          </Button>
        </Box>
      </DialogFooter>
    </>
  );
}
